window.LoadMoreProducts = class {
  constructor() {
    this.setupInitialState();
    this.nextPageContent = null;
    this.preloadNextPage();
  }

  cleanup() {
    if (this.loadMoreButton) {
      const loadMoreSection = this.loadMoreButton.closest('.load-more-section');
      if (loadMoreSection) {
        loadMoreSection.remove();
      }
    }
  }

  setupInitialState() {
    this.productGrid = document.querySelector('#product-grid');
    if (!this.productGrid) {
      console.warn('Product grid not found');
      return;
    }

    if (!this.productGrid.dataset.totalProducts || !this.productGrid.dataset.productsPerPage) {
      console.warn('Missing required data attributes on product grid:', this.productGrid.dataset);
      return;
    }

    this.totalProducts = parseInt(this.productGrid.dataset.totalProducts) || 0;
    this.productsPerPage = parseInt(this.productGrid.dataset.productsPerPage) || 16;
    this.currentPage = 1;
    this.totalPages = Math.ceil(this.totalProducts / this.productsPerPage);
    this.currentProducts = document.querySelectorAll('#product-grid .grid__item').length;

    if (this.totalProducts > this.productsPerPage) {
      this.initialize();
    } else {
      console.log('No pagination needed:', { total: this.totalProducts, perPage: this.productsPerPage });
    }
  }

  initialize() {
    const paginationElement = document.querySelector('.pagination');
    if (paginationElement) {
      paginationElement.style.display = 'none';
    }

    // Remove any existing load more sections before creating a new one
    const existingLoadMore = document.querySelector('.load-more-section');
    if (existingLoadMore) {
      existingLoadMore.remove();
    }

    this.addCustomStyles();
    this.createLoadMoreButton();
  }

  addCustomStyles() {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      .load-more-section {
        width: 100%;
        max-width: var(--page-width);
        margin: 0 auto;
        padding: 0 1.5rem;
        clear: both;
      }

      .load-more-wrapper {
        width: 100%;
        text-align: center;
        padding: 2rem 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1rem;
      }

      .custom-load-more {
        background: transparent;
        border: 2px solid #FF69B4 !important;
        border-radius: 30px;
        padding: 15px 35px;
        cursor: pointer;
        transition: all 0.3s ease;
        font-family: inherit;
        font-size: 1.8rem;
        font-weight: 700;
        line-height: 1.2;
        color: inherit;
        box-shadow: none !important;
        min-width: 250px;
        margin: 0 auto;
        position: relative;
      }
      
      .custom-load-more:hover {
        background: rgba(var(--color-button), 0.04);
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1) !important;
      }
      
      .custom-load-more:active {
        transform: translateY(0);
      }

      .custom-load-more.loading {
        background: transparent !important;
        color: transparent !important;
      }
      
      .custom-load-more.loading::after {
        content: '';
        position: absolute;
        width: 20px;
        height: 20px;
        top: 50%;
        left: 50%;
        margin: -10px 0 0 -10px;
        border: 3px solid #FF69B4;
        border-radius: 50%;
        border-top-color: transparent;
        animation: loadingSpin 0.6s linear infinite;
      }

      .product-count {
        font-size: 1.4rem;
        color: rgba(var(--color-foreground), 0.75);
        margin-top: 0.5rem;
      }

      @keyframes loadingSpin {
        to { transform: rotate(360deg); }
      }

      @media screen and (min-width: 750px) {
        .load-more-section {
          padding: 0 5rem;
        }
      }

      .grid__item.fade-in {
        animation: fadeIn 0.5s ease-out;
      }

      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `;
    document.head.appendChild(styleSheet);
  }

  createLoadMoreButton() {
    const mainContent = this.productGrid.closest('main') || this.productGrid.parentElement;
    
    const section = document.createElement('div');
    section.className = 'load-more-section';
    
    const wrapper = document.createElement('div');
    wrapper.className = 'load-more-wrapper';
    
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'custom-load-more';
    button.textContent = 'SHOW MORE PRODUCTS';

    const counter = document.createElement('div');
    counter.className = 'product-count';
    this.updateProductCount(counter);

    button.addEventListener('click', () => this.loadMore());
    
    wrapper.appendChild(button);
    wrapper.appendChild(counter);
    section.appendChild(wrapper);
    
    const productGridSection = this.productGrid.closest('section') || this.productGrid.parentElement;
    productGridSection.insertAdjacentElement('afterend', section);
    
    this.loadMoreButton = button;
    this.productCounter = counter;
  }

  updateProductCount(counter = this.productCounter) {
    if (counter) {
      this.currentProducts = document.querySelectorAll('#product-grid .grid__item').length;
      counter.textContent = `${this.currentProducts} OUT OF ${this.totalProducts} PRODUCTS`;
    }
  }

  async preloadNextPage() {
    if (this.currentPage >= this.totalPages) return;
    
    try {
      const nextPage = this.currentPage + 1;
      const url = new URL(window.location.href);
      url.searchParams.set('page', nextPage);

      const response = await fetch(url.toString());
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      this.nextPageContent = doc.querySelectorAll('#product-grid .grid__item');
    } catch (error) {
      console.error('Error preloading next page:', error);
    }
  }

  async loadMore() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.loadMoreButton.classList.add('loading');
    this.loadMoreButton.disabled = true;

    try {
      if (this.nextPageContent) {
        this.nextPageContent.forEach(product => {
          const clone = product.cloneNode(true);
          clone.classList.add('fade-in');
          this.productGrid.appendChild(clone);
        });
      } else {
        const nextPage = this.currentPage + 1;
        const url = new URL(window.location.href);
        url.searchParams.set('page', nextPage);

        const response = await fetch(url.toString());
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newProducts = doc.querySelectorAll('#product-grid .grid__item');
        newProducts.forEach(product => {
          const clone = product.cloneNode(true);
          clone.classList.add('fade-in');
          this.productGrid.appendChild(clone);
        });
      }

      this.currentPage++;
      this.updateProductCount();
      
      this.nextPageContent = null;
      if (this.currentPage < this.totalPages) {
        this.preloadNextPage();
        this.loadMoreButton.classList.remove('loading');
        this.loadMoreButton.textContent = 'SHOW MORE PRODUCTS';
      } else {
        this.loadMoreButton.closest('.load-more-section').style.display = 'none';
      }
    } catch (error) {
      this.loadMoreButton.classList.remove('loading');
      this.loadMoreButton.textContent = 'Error. Try Again';
    } finally {
      this.isLoading = false;
      this.loadMoreButton.disabled = false;
    }
  }
};